import { createForm } from "@tanstack/solid-form";
import { BiSolidCheckCircle } from "solid-icons/bi";
import { type Component, createSignal, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSubmitContactSales } from "@/domains/business/contact-sales/api";
import {
  type ContactSalesFormValues,
  contactSalesSchema,
  ROLE_OPTIONS,
  type RoleOption,
  TEAM_SIZE_OPTIONS,
  type TeamSizeOption,
} from "@/domains/business/contact-sales/validators/contactSales";

type ContactSalesModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ContactSalesModal: Component<ContactSalesModalProps> = (props) => {
  const { t } = useI18n();
  const mutation = useSubmitContactSales();
  const [submitted, setSubmitted] = createSignal(false);
  const [submitError, setSubmitError] = createSignal<string | null>(null);

  const form = createForm(() => ({
    defaultValues: {
      name: "",
      company_name: "",
      role: undefined,
      email: "",
      phone: "",
      team_size: undefined,
      message: "",
    } as unknown as ContactSalesFormValues,
    // Full-schema gate on submit: invalid/untouched required fields surface their
    // errors via FieldInfo and the submission is blocked before hitting the BE.
    validators: { onSubmit: contactSalesSchema },
    onSubmit: async ({ value }) => {
      const parsed = contactSalesSchema.safeParse(value);
      if (!parsed.success) return;
      setSubmitError(null);
      try {
        await mutation.mutateAsync(parsed.data);
        setSubmitted(true);
      } catch {
        setSubmitError(t("contact_sales.error_message"));
      }
    },
  }));

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
      setSubmitted(false);
      setSubmitError(null);
      props.onClose();
    }
  };

  return (
    <Dialog open={props.isOpen} onOpenChange={handleOpenChange}>
      <DialogContent class="sm:max-w-2xl">
        <Show
          when={!submitted()}
          fallback={
            <div class="flex flex-col items-center gap-6 text-center py-8">
              <BiSolidCheckCircle class="w-14 h-14 text-green-500" />
              <div class="space-y-2">
                <h2 class="text-xl font-bold text-gray-900">
                  {t("contact_sales.success_heading")}
                </h2>
                <p class="text-gray-600 text-sm">
                  {t("contact_sales.success_message")}
                </p>
              </div>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t("contact_sales.success_close")}
              </Button>
            </div>
          }
        >
          <DialogHeader>
            <DialogTitle>{t("contact_sales.page_title")}</DialogTitle>
            <DialogDescription>
              {t("contact_sales.page_subtitle")}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            class="space-y-4"
          >
            <div class="grid grid-cols-2 gap-4 w-full">
              <form.Field
                name="name"
                validators={{ onChange: contactSalesSchema.shape.name }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("contact_sales.field.name")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder={t("contact_sales.placeholder.name")}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>

              <form.Field
                name="company_name"
                validators={{
                  onChange: contactSalesSchema.shape.company_name,
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("contact_sales.field.company_name")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder={t("contact_sales.placeholder.company_name")}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
            </div>

            <div class="grid grid-cols-2 gap-4 w-full">
              <form.Field
                name="role"
                validators={{ onChange: contactSalesSchema.shape.role }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("contact_sales.field.role")}
                    </FieldContainerLabel>
                    <Select
                      value={field().state.value}
                      onChange={(value) => {
                        if (value) field().handleChange(value as RoleOption);
                      }}
                      options={[...ROLE_OPTIONS]}
                      placeholder={t("contact_sales.placeholder.role")}
                      itemComponent={(item) => (
                        <SelectItem {...item}>{item.item.rawValue}</SelectItem>
                      )}
                    >
                      <SelectTrigger>
                        <SelectValue>
                          <Show
                            when={field().state.value}
                            fallback={t("contact_sales.placeholder.role")}
                          >
                            {field().state.value}
                          </Show>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent />
                    </Select>
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>

              <form.Field
                name="email"
                validators={{ onChange: contactSalesSchema.shape.email }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("contact_sales.field.email")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="email"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder={t("contact_sales.placeholder.email")}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
            </div>

            <div class="grid grid-cols-2 gap-4 w-full">
              <form.Field name="phone">
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("contact_sales.field.phone")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="tel"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder={t("contact_sales.placeholder.phone")}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>

              <form.Field name="team_size">
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("contact_sales.field.team_size")}
                    </FieldContainerLabel>
                    <Select
                      value={field().state.value}
                      onChange={(value) => {
                        field().handleChange(
                          (value as TeamSizeOption) ?? undefined,
                        );
                      }}
                      options={[...TEAM_SIZE_OPTIONS]}
                      placeholder={t("contact_sales.placeholder.team_size")}
                      itemComponent={(item) => (
                        <SelectItem {...item}>{item.item.rawValue}</SelectItem>
                      )}
                    >
                      <SelectTrigger>
                        <SelectValue>
                          <Show
                            when={field().state.value}
                            fallback={t("contact_sales.placeholder.team_size")}
                          >
                            {field().state.value}
                          </Show>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent />
                    </Select>
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
            </div>

            <form.Field name="message">
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t("contact_sales.field.message")}
                  </FieldContainerLabel>
                  <textarea
                    id={field().name}
                    name={field().name}
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={() => field().handleBlur()}
                    class="flex min-h-[120px] w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    placeholder={t("contact_sales.placeholder.message")}
                    rows={4}
                  />
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>

            <Show when={submitError()}>
              {(error) => (
                <p class="text-sm text-red-500 text-center">{error()}</p>
              )}
            </Show>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={mutation.isPending}
              >
                {t("contact_sales.cancel")}
              </Button>
              <form.Subscribe
                selector={(state) => ({
                  isSubmittable: contactSalesSchema.safeParse(state.values)
                    .success,
                })}
              >
                {(state) => (
                  <Button
                    type="submit"
                    disabled={mutation.isPending || !state().isSubmittable}
                  >
                    {mutation.isPending
                      ? t("contact_sales.submitting")
                      : t("contact_sales.submit")}
                  </Button>
                )}
              </form.Subscribe>
            </DialogFooter>
          </form>
        </Show>
      </DialogContent>
    </Dialog>
  );
};
